import { css } from '@emotion/react';
import React, { useContext, useEffect, useState } from 'react';
import t from 'react-translate';
import { AngularContext } from 'react-app';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { closeEditTranslationComponent } from 'redux/actions/edit-translations';
import { useAppDispatch } from 'redux/store';
import useAngularExpression from 'shared/hooks/use-angular-expression';
import { gray5, gray7, white } from 'styles/global_defaults/colors';
import { TranslationInfo } from 'redux/schemas/models/edit-translations';
import { ComponentType, RichTextType } from 'redux/schemas/models/lecture-component';
import { doubleSpacing, halfSpacing, largeSpacing, modalOverlayZIndex, quarterSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import NvIcon from 'shared/components/nv-icon';
import EditableSimpleComponent from 'lecture_pages/directives/components/editable-components/editable-simple-component';
import EditableRichText from 'lecture_pages/directives/components/editable-components/editable-rich-text';
import EditableHTMLText from 'lecture_pages/directives/components/editable-components/editable-html-text';
import EditableAccordionSections from 'lecture_pages/directives/components/editable-components/editable-accordion-sections';
import usePreventLecturePageNavigation from 'lecture_pages/hooks/use-prevent-lecture-page-navigation';
import EditableDiscussion from 'lecture_pages/directives/components/editable-components/editable-discussion';
import EditableFeedbackForAssignment from 'lecture_pages/directives/components/editable-components/editable-feedback-for-assignment';
import EditableProfileCompletion from 'lecture_pages/directives/components/editable-components/editable-profile-completion';
import EditableAssignment from 'lecture_pages/directives/components/editable-components/editable-assignment';
import EditablePoll from 'lecture_pages/directives/components/editable-components/editable-poll';
import EditableCourseSections from 'lecture_pages/directives/components/editable-components/editable-course-sections';
import EditableThirdPartyLink from 'lecture_pages/directives/components/editable-components/editable-third-party-link';
import EditableAudioVideoList from 'lecture_pages/directives/components/editable-components/editable-audio-video-list';
import EditableQuiz from 'lecture_pages/directives/components/editable-components/editable-quiz';
import EditableSurvey from 'lecture_pages/directives/components/editable-components/editable-survey';
import EditableSingleViewQuiz from 'lecture_pages/directives/components/editable-components/editable-single-view-quiz';
import EditableFile from 'lecture_pages/directives/components/editable-components/editable-file';

const EditTranslationComponent = () => {
  const containerClass = 'edit-translation';
  const [saveHandler, setSaveHandler] = useState(null);
  const [savingDisabled, setSavingDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const editTranslationInfo: TranslationInfo = useSelector((state) => state.app.editTranslationInfo);
  const dispatch = useAppDispatch();
  const { injectServices, $scope } = useContext(AngularContext);
  const [$state] = injectServices(['$state', '$timeout', 'CurrentUserManager', 'CurrentCourseManager']);
  const currentStateName = useAngularExpression(() => $state.current.name);
  const isLecturePage = currentStateName === 'lecture-page';
  const editTranslationComponentStyles = css`
    position: absolute;
    right: 0;
    bottom: 0;
    ${isLecturePage
    ? css`
        bottom: 70px; // lecture-page-bottom-panel space
      `
    : css`
        bottom: 0;
      `}
    .edit-translation-component {
      position: relative;
      display: flex;
      width: 640px;
      min-height: 300px;
      max-height: 80vh;
      flex-direction: column;
      align-items: center;
      z-index: ${modalOverlayZIndex};
      background-color: ${white};
      box-shadow: 0px 10px 10px 0px rgba(29, 33, 38, 0.10);
      border-radius: ${halfSpacing}px ${halfSpacing}px 0px 0px;
      border: 1px solid ${gray5};
      .header {
        display: flex;
        justify-content: space-between;
        padding: ${standardSpacing}px ${largeSpacing}px;
        border-radius: ${halfSpacing}px ${halfSpacing}px 0px 0px;
        align-items: center;
        align-self: stretch;
        background: ${gray7};
        .title {
          display: flex;
          span {
            margin-left: ${quarterSpacing}px;
          }
        }
        .buttons {
          button {
            padding: ${quarterSpacing}px ${threeQuartersSpacing}px;
          }
          .save-button {
            margin-left: ${halfSpacing}px;
          }
        }
      }
      .editable-lecture-component {
        position: relative;
        padding: ${standardSpacing}px ${doubleSpacing}px ${doubleSpacing}px;
        width: 100%;
        height: 100%;
        margin: auto;
        overflow: auto;
      }
    }
  `;

  useEffect(() => {
    const deRegisterStateChangeStart = $scope.StateManager.registerStateChangeStart(
      () => !savingDisabled,
      'shared/templates/modal-navigate-away-unsaved-changes.html',
      'FORM.UNSAVED_CHANGES.NAVIGATE_AWAY_CHANGES',
    );

    return () => {
      deRegisterStateChangeStart();
    };
  }, [$scope.StateManager, savingDisabled]);

  usePreventLecturePageNavigation(() => !savingDisabled);

  const removeEditTranslationComponent = () => {
    setSavingDisabled(false);
    setSaveHandler(null);
    dispatch(closeEditTranslationComponent());
  };

  const getEditableLectureComponent = (editInfo) => {
    let component = null;
    switch (editTranslationInfo.type) {
      case ComponentType.TEXT_WITH_IMAGE_BKG:
        component = <EditableRichText editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} withViewOptions containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.TEXT_WITH_IMAGE_SIDE:
      case ComponentType.TEXT_WITH_IMAGE_TOP:
        component = <EditableRichText editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.ACCORDION:
        component = <EditableAccordionSections editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.RICH_TEXT:
        if (editTranslationInfo.trueType === RichTextType.FULL) {
          component = <EditableHTMLText editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        } else {
          component = <EditableRichText editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        }
        break;
      case ComponentType.PROFILE_COMPLETION:
        component = <EditableProfileCompletion editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.TOPIC:
      case ComponentType.TEAM_DISCUSSION:
        component = <EditableDiscussion editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.PUBLIC_PEER_EVALUATION:
      case ComponentType.PRIVATE_PEER_EVALUATION:
        component = <EditableFeedbackForAssignment editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.EXERCISE:
        component = <EditableAssignment editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} setIsLoading={setIsLoading} />;
        break;
      case ComponentType.POLL:
        component = <EditablePoll editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.STYLED_LINK:
      case ComponentType.HEADER_STYLE1:
      case ComponentType.HEADER_STYLE2:
      case ComponentType.HEADER_STYLE3:
        component = <EditableSimpleComponent editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} />;
        break;
      case ComponentType.EXTERNAL_TOOL:
        component = <EditableThirdPartyLink editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} />;
        break;
      case ComponentType.VIDEO:
      case ComponentType.AUDIO:
        component = <EditableAudioVideoList editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} />;
        break;
      case ComponentType.QUIZ:
        component = <EditableQuiz editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.SURVEY:
        component = <EditableSurvey editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.PROGRESSIVE_QUIZ:
        component = <EditableSingleViewQuiz editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} containerClass={`.${containerClass}`} />;
        break;
      case ComponentType.ATTACHMENT:
        component = <EditableFile editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} setIsLoading={setIsLoading} />;
        break;
      default:
        component = <EditableCourseSections editInfo={editInfo} onSaveRef={setSaveHandler} setSavingDisabled={setSavingDisabled} />;
    }
    return component;
  };

  const saveChanges = () => {
    saveHandler?.();
  };
  return (
    <div className={containerClass} css={editTranslationComponentStyles}>
      {
        editTranslationInfo
        && (
          <div className='edit-translation-component'>
            <div className='header'>
              <div className='title'>
                <NvIcon icon='translate' size='small' />
                <span className='font-weight-bolder'>
                  { t.AUTOMATED_TRANSLATION.OPTION_BAR.EDIT_TRANSLATION_TOOLTIP() }
                </span>
              </div>
              <div className='buttons'>
                <Button className='cancel-button' variant='secondary' onClick={removeEditTranslationComponent} disabled={isLoading}>
                  { t.FORM.CANCEL() }
                </Button>
                <Button className='save-button' onClick={saveChanges} disabled={savingDisabled || isLoading}>
                  { t.FORM.SAVE() }
                </Button>
              </div>
            </div>
            <div className='editable-lecture-component d-flex'>
              {
                getEditableLectureComponent(editTranslationInfo)
              }
            </div>
          </div>
        )
      }
    </div>
  );
};

export default EditTranslationComponent;
